import { useCallback, useMemo } from 'react'
import { Box, Container, Grid, Typography } from '@mui/material'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'

import Form from '@/components/base/form'
import Spacer from '@/components/sections/spacer'
import { useNotification } from '@/contexts/notification'

import { useAuth } from '@/api/auth'
import { WPCF7FormField, WPPageData } from '@/types'

export type ResetPasswordProps = {
  pageProps?: Omit<WPPageData, 'acf'> & {
    acf: {
      title?: string
      text?: string
      form?: {
        form: {
          fields: WPCF7FormField[]
          button: {
            text: string
          }
        }
      }
    }
  }
}

const ResetPassword = ({ pageProps }: ResetPasswordProps) => {
  const router = useRouter()
  const { token, email } = router.query
  const acf = useMemo(() => pageProps?.acf, [pageProps])
  const form = useMemo(() => acf?.form?.form, [acf])
  const fields = useMemo(() => form?.fields || [], [form])

  const { resetPassword } = useAuth()
  const { notifySuccess, notifyError } = useNotification()

  const handleSubmit = useCallback(
    async (values: Record<string, any>) => {
      try {
        if (!token || typeof token !== 'string') {
          notifyError('Token di reset mancante')
          return false
        }
        if (!email || typeof email !== 'string') {
          notifyError('Email mancante')
          return false
        }

        // Extract password and password_confirmation from form values
        let password = ''
        let password_confirmation = ''
        fields.forEach((field) => {
          const inputKey = `input_${field.id}`
          if (field.adminLabel === 'password' && values[inputKey] !== undefined) {
            password = values[inputKey]
          }
          if (field.adminLabel === 'password_confirmation' && values[inputKey] !== undefined) {
            password_confirmation = values[inputKey]
          }
        })

        const response = await resetPassword({ email, token, password, password_confirmation })
        if (response?.message) {
          notifySuccess(response.message)
          router.push('/login')
        }
        return true
      } catch (error: any) {
        notifyError(error.message || 'Request failed')
        return false
      }
    },
    [fields, resetPassword, notifyError, notifySuccess, token, email, router]
  )

  return (
    <>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 6 }} offset={{ sm: 1, md: 3 }}>
            <Box
              sx={(theme) => ({
                p: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                borderRadius: theme.shape.borderRadius4,
                backgroundColor: theme.palette.surface.background,
                boxShadow: theme.shadows[2]
              })}
            >
              {acf?.title && <Typography variant="h4">{acf.title}</Typography>}
              <Spacer size="xxs" />
              {acf?.text && (
                <Typography variant="textRegular" color="text.primary">
                  {parse(acf.text)}
                </Typography>
              )}
              <Spacer size="xs" />
              <Form
                form_id={form?.fields[0]?.formId?.toString() || ''}
                fields={form?.fields || []}
                submit={{ text: form?.button.text || 'Submit', color: 'primary' }}
                onSubmit={handleSubmit}
                customEndpoint={true}
                useGravity={false}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Spacer size="2xl" />
    </>
  )
}

export default ResetPassword
